import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, EnvelopeSimple, PaperPlaneTilt, Warning, CheckCircle, XCircle, MinusCircle, ArrowBendDownRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const TRIGGER_LABELS = {
  signature_submitted: "Schadenmeldung unterschrieben",
  deposit_paid: "Anzahlung bezahlt",
  self_pay_estimate: "Selbstzahler-Anfrage",
  review_request: "Bewertungs-Mail",
  invoice_manual: "Rechnung (manuell)",
  tint_booking: "Tönungs-Buchung",
  sattlerei_booking: "Sattlerei-Buchung",
  sattlerei_pickup_ready: "Sattlerei abholbereit",
  test_email: "Test-E-Mail",
};

const SKIP_REASONS = {
  no_api_key: "Kein Resend-API-Key hinterlegt",
  no_customer_email: "Keine Kunden-E-Mail angegeben",
  no_email: "Keine E-Mail-Adresse angegeben",
  no_credentials: "Keine Zugangsdaten (SMS)",
  not_insurance: "Kein Versicherungsfall",
  not_self_pay: "Kein Selbstzahler",
  no_appointment: "Kein Termin",
  no_forms: "Keine Formulare",
};

const CHANNELS = [
  ["customer_email", "Kunde"],
  ["owner_email", "Werkstatt"],
  ["insurance_forms", "Vers.-Formulare"],
  ["invoice_email", "Rechnung"],
  ["customer_sms", "SMS"],
];

function ChannelChip({ label, res }) {
  if (!res || typeof res !== "object") return null;
  const s = res.status;
  const redirected = s === "sent" && res.original_to && res.delivered_to && res.original_to !== res.delivered_to;
  let icon, cls, text;
  if (s === "sent" && redirected) {
    icon = <ArrowBendDownRight size={13} weight="bold" />;
    cls = "bg-amber-50 text-amber-700 border-amber-200";
    text = `umgeleitet an ${res.delivered_to}`;
  } else if (s === "sent") {
    icon = <CheckCircle size={13} weight="fill" />;
    cls = "bg-emerald-50 text-emerald-700 border-emerald-200";
    text = `gesendet an ${res.delivered_to || res.original_to || ""}`.trim();
  } else if (s === "failed") {
    icon = <XCircle size={13} weight="fill" />;
    cls = "bg-red-50 text-red-700 border-red-200";
    text = res.error ? `fehlgeschlagen: ${String(res.error).slice(0, 120)}` : "fehlgeschlagen";
  } else {
    icon = <MinusCircle size={13} />;
    cls = "bg-slate-50 text-slate-500 border-slate-200";
    text = SKIP_REASONS[res.reason] || res.reason || "übersprungen";
  }
  return (
    <div className={`inline-flex items-start gap-1.5 rounded-lg border px-2 py-1 text-[11px] leading-snug ${cls}`}>
      {icon}
      <span>
        <strong className="font-semibold">{label}:</strong> {text}
        {redirected && res.original_to ? <span className="block text-[10px] opacity-80">eigentlich für {res.original_to}</span> : null}
      </span>
    </div>
  );
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    }) + " Uhr";
  } catch {
    return iso || "—";
  }
}

export default function AdminEmails() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async (skip = 0) => {
    try {
      const r = await api.get(`/admin/email-logs?limit=50&skip=${skip}`);
      setItems((prev) => (skip === 0 ? r.data.items : [...prev, ...r.data.items]));
      setTotal(r.data.total);
      setConfig(r.data.config);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Protokoll konnte nicht geladen werden");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) {
      navigate("/admin/login");
      return;
    }
    load(0);
  }, [navigate, load]);

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/email-test");
      if (r.data.ok) {
        toast.success(`Test-E-Mail gesendet an ${r.data.result?.delivered_to}`);
      } else {
        toast.error(`Test fehlgeschlagen: ${r.data.result?.error || r.data.result?.reason || "unbekannt"}`);
      }
      load(0);
    } catch {
      toast.error("Test-E-Mail fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <Button onClick={() => navigate("/admin")} variant="ghost" size="icon" className="text-slate-300 hover:text-white hover:bg-slate-800 shrink-0" data-testid="emails-back-btn">
              <ArrowLeft size={20} />
            </Button>
            <div className="w-9 h-9 rounded-lg bg-sky-600 hidden sm:flex items-center justify-center shrink-0">
              <EnvelopeSimple size={20} weight="fill" color="white" />
            </div>
            <div className="min-w-0">
              <div className="font-bold truncate">E-Mail-Protokoll</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-300 -mt-0.5 hidden sm:block">Alle Versandvorgänge</div>
            </div>
          </div>
          <Button onClick={sendTest} disabled={testing} className="bg-sky-600 hover:bg-sky-700 text-white shrink-0" data-testid="send-test-email-btn">
            <PaperPlaneTilt size={16} className="mr-1.5" weight="fill" />
            {testing ? "Sendet…" : "Test-E-Mail"}
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {config && !config.configured && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 flex gap-3" data-testid="email-config-banner-missing">
            <Warning size={22} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              <strong>Kein Resend-API-Key hinterlegt — es werden KEINE E-Mails versendet!</strong>
              <div className="mt-1 text-red-700">Alle Versandversuche werden als „übersprungen" protokolliert, bis der Key gesetzt ist.</div>
            </div>
          </div>
        )}
        {config && config.configured && config.redirect_active && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex gap-3" data-testid="email-config-banner-redirect">
            <Warning size={22} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <strong>Umleitungs-Modus aktiv:</strong> Absender ist die unverifizierte Adresse <code className="bg-amber-100 px-1 rounded">{config.sender}</code>.
              Alle Mails gehen an <strong>{config.notification_email}</strong> — Kunden erhalten <u>keine</u> direkten E-Mails,
              bis die Domain bei Resend verifiziert und der Absender umgestellt ist.
            </div>
          </div>
        )}
        {config && config.configured && !config.redirect_active && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3" data-testid="email-config-banner-ok">
            <CheckCircle size={22} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-800">
              <strong>Direktversand aktiv</strong> über <code className="bg-emerald-100 px-1 rounded">{config.sender}</code> — Kunden erhalten ihre E-Mails direkt.
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">Protokoll</h2>
          <span className="text-sm text-slate-500" data-testid="email-log-count">{total} Einträge</span>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400">Lade Protokoll…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500" data-testid="email-log-empty">
            Noch keine E-Mail-Vorgänge protokolliert.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((l) => (
              <div key={l.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`email-log-${l.id}`}>
                <div className="flex flex-wrap items-center gap-2 mb-2.5">
                  <Badge className="bg-slate-900 text-white hover:bg-slate-900">{TRIGGER_LABELS[l.trigger] || l.trigger}</Badge>
                  {l.reference && (
                    <button
                      onClick={() => l.claim_id && navigate(`/admin/claims/${l.claim_id}`)}
                      className={`text-xs font-mono font-semibold ${l.claim_id ? "text-blue-600 hover:underline" : "text-slate-600"}`}
                      data-testid={`email-log-ref-${l.id}`}
                    >
                      {l.reference}
                    </button>
                  )}
                  {l.reference_name && <span className="text-xs text-slate-500">· {l.reference_name}</span>}
                  <span className="text-xs text-slate-400 ml-auto">{fmtDate(l.created_at)}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {CHANNELS.map(([key, label]) => (
                    <ChannelChip key={key} label={label} res={l[key]} />
                  ))}
                </div>
              </div>
            ))}
            {items.length < total && (
              <div className="text-center pt-2">
                <Button variant="outline" onClick={() => load(items.length)} data-testid="email-log-load-more">
                  Mehr laden ({items.length} / {total})
                </Button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
